import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface BlackHoleType {
  name: string;
  mass: string;
  example: string;
  icon: string;
  color: string;
}

interface KeyConcept {
  name: string;
  description: string;
  icon: string;
}

interface FamousBlackHole {
  name: string;
  distance: string;
  mass: string;
  year: string;
}

interface BlackHoleFact {
  label: string;
  icon: string;
}

interface BlackHoleTemplateProps {
  topic: string;
  subtitle: string;
  blackHoleTypes: BlackHoleType[];
  keyConcepts: KeyConcept[];
  famousBlackHoles: FamousBlackHole[];
  blackHoleFacts: BlackHoleFact[];
  accretionData: { angle: number; radius: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: BlackHoleTemplateProps = {
  topic: '黑洞与时空弯曲',
  subtitle: 'Black Holes & Spacetime Curvature',
  blackHoleTypes: [
    {
      name: '恒星级黑洞',
      mass: '5-100 太阳质量',
      example: '天鹅座X-1',
      icon: '⭐',
      color: '#8b5cf6',
    },
    {
      name: '中等质量黑洞',
      mass: '100-10⁵ 太阳质量',
      example: 'HLX-1',
      icon: '🌟',
      color: '#a78bfa',
    },
    {
      name: '超大质量黑洞',
      mass: '10⁶-10¹⁰ 太阳质量',
      example: '人马座A* / M87*',
      icon: '🌀',
      color: '#c4b5fd',
    },
    {
      name: '原初黑洞',
      mass: '极小-极大(理论)',
      example: '宇宙大爆炸遗留(假说)',
      icon: '💫',
      color: '#ddd6fe',
    },
  ],
  keyConcepts: [
    {
      name: '事件视界',
      description: '光都无法逃脱的临界边界',
      icon: '🔲',
    },
    {
      name: '奇点',
      description: '密度无限大的时空中心',
      icon: '🎯',
    },
    {
      name: '霍金辐射',
      description: '黑洞量子效应缓慢蒸发',
      icon: '✨',
    },
    {
      name: '引力红移',
      description: '强引力场中光频率降低',
      icon: '🔴',
    },
  ],
  famousBlackHoles: [
    { name: '人马座A*', distance: '26,000 光年', mass: '400万太阳质量', year: '2022 成像' },
    { name: 'M87*', distance: '5500万 光年', mass: '65亿太阳质量', year: '2019 首张照片' },
    { name: '天鹅座X-1', distance: '6,070 光年', mass: '21太阳质量', year: '1964 发现' },
    { name: 'TON 618', distance: '104亿 光年', mass: '660亿太阳质量', year: '已知最大之一' },
  ],
  blackHoleFacts: [
    { label: '时间膨胀', icon: '⏰' },
    { label: '引力透镜', icon: '🔍' },
    { label: '潮汐力', icon: '🌊' },
    { label: '喷流', icon: '🚀' },
  ],
  accretionData: [
    { angle: 0, radius: 80 },
    { angle: 45, radius: 95 },
    { angle: 90, radius: 110 },
    { angle: 135, radius: 100 },
    { angle: 180, radius: 85 },
    { angle: 225, radius: 105 },
    { angle: 270, radius: 115 },
    { angle: 315, radius: 90 },
  ],
  stats: [
    { value: '1,560光年', label: '最近黑洞距地球距离' },
    { value: '660亿M☉', label: '已知最大黑洞质量' },
    { value: '2019', label: '人类首次拍摄黑洞照片' },
  ],
  bottomQuote:
    '黑洞是物理定律崩塌之处——在那里，时间停止流逝，空间不再有意义，人类认知触及宇宙的极限',
};

export const BlackHoleTemplate: React.FC<
  Partial<BlackHoleTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const typeStart = 30;
  const conceptStart = 160;
  const svgStart = 200;
  const famousStart = 300;
  const factStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const diskSpin = interpolate(frame, [svgStart, svgStart + 300], [0, 360], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const diskOpacity = interpolate(
    frame,
    [svgStart, svgStart + 50],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a0533 0%, #0c0015 50%, #020007 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Swirling star particles drawn toward center */}
      {Array.from({ length: 60 }).map((_, i) => {
        const baseX = (i * 311) % 1920;
        const baseY = (i * 179) % 1080;
        const centerX = 960;
        const centerY = 540;
        const pullStrength = 0.0003 * frame;
        const clampedPull = Math.min(pullStrength, 0.15);
        const x = baseX + (centerX - baseX) * clampedPull;
        const y = baseY + (centerY - baseY) * clampedPull;
        const size = 1.5 + (i % 4);
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.05 + i * 0.8);
        const colors = ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#fbbf24'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#5b21b6',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#ddd6fe',
            flexShrink: 0,
          }}
        >
          🌌 天体物理
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ede9fe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Black Hole Types ===== */}
      <div style={{ position: 'absolute', top: 130, left: 36, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          黑洞分类
        </div>
        {p.blackHoleTypes.map((bh, i) => {
          const s = spring({
            frame: frame - (typeStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#150a2e',
                borderRadius: 14,
                padding: '12px 16px',
                border: `1.5px solid ${bh.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: `${bh.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 30,
                  flexShrink: 0,
                  boxShadow: `0 0 18px ${bh.color}30`,
                }}
              >
                {bh.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: bh.color }}>
                    {bh.name}
                  </span>
                  <span style={{ fontSize: 22, color: '#94a3b8' }}>
                    {bh.mass}
                  </span>
                </div>
                <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>
                  {bh.example}
                </div>
              </div>
            </div>
          );
        })}

        {/* Key Concepts */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            核心概念
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.keyConcepts.map((kc, i) => {
              const s = spring({
                frame: frame - (conceptStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#150a2e',
                    borderRadius: 12,
                    padding: '12px 10px',
                    border: '1px solid #5b21b640',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 28 }}>{kc.icon}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#a78bfa' }}>{kc.name}</span>
                  </div>
                  <div style={{ fontSize: 20, color: '#64748b', marginTop: 2 }}>
                    {kc.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Accretion Disk SVG ===== */}
      <div style={{ position: 'absolute', top: 130, left: 540, width: 920 }}>
        <div
          style={{
            fontSize: 26,
            color: '#c4b5fd',
            marginBottom: 14,
            letterSpacing: '0.08em',
            fontWeight: 700,
            marginLeft: 80,
          }}
        >
          吸积盘与黑洞结构
        </div>
        <svg width="900" height="580" viewBox="0 0 900 580" opacity={diskOpacity}>
          <defs>
            <radialGradient id="bhGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.38" />
              <stop offset="35%" stopColor="#7c3aed" stopOpacity="0.18" />
              <stop offset="65%" stopColor="#5b21b6" stopOpacity="0.06" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="bhCenter" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#000000" stopOpacity="1" />
              <stop offset="70%" stopColor="#050008" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#1a0533" stopOpacity="0.6" />
            </radialGradient>
            <radialGradient id="diskHeat" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.22" />
              <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.09" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="jetUp" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="jetDown" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Outer glow */}
          <circle cx="450" cy="290" r="260" fill="url(#bhGlow)" />

          {/* Disk heat halo */}
          <ellipse cx="450" cy="290" rx="240" ry="86" fill="url(#diskHeat)" />

          {/* Accretion disk rings */}
          {[230, 200, 170, 140, 112].map((r, i) => {
            const ringOpacity = 0.13 + i * 0.07;
            const strokeColors = ['#fbbf24', '#f59e0b', '#d97706', '#a78bfa', '#8b5cf6'];
            return (
              <React.Fragment key={`ring-${i}`}>
                <ellipse
                  cx="450"
                  cy="290"
                  rx={r}
                  ry={r * 0.35}
                  fill="none"
                  stroke={strokeColors[i]}
                  strokeWidth={5 - i * 0.7}
                  opacity={ringOpacity}
                  transform={`rotate(${diskSpin * (0.3 + i * 0.08)}, 450, 290)`}
                />
                {i < 3 && (
                  <ellipse
                    cx="450"
                    cy="290"
                    rx={r - 7}
                    ry={(r - 7) * 0.35}
                    fill="none"
                    stroke={strokeColors[i]}
                    strokeWidth={1.8}
                    opacity={ringOpacity * 0.45}
                    transform={`rotate(${diskSpin * (0.35 + i * 0.1)}, 450, 290)`}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* Spiral arms */}
          {[0, 120, 240].map((offset, i) => {
            const armPath = Array.from({ length: 30 }, (_, j) => {
              const angle = ((offset + j * 12 + diskSpin * 0.5) * Math.PI) / 180;
              const r = 65 + j * 7.5;
              const x = 450 + r * Math.cos(angle);
              const y = 290 + r * 0.35 * Math.sin(angle);
              return j === 0 ? `M${x},${y}` : `L${x},${y}`;
            }).join(' ');
            return (
              <path
                key={`arm-${i}`}
                d={armPath}
                fill="none"
                stroke="#fbbf2450"
                strokeWidth="3"
              />
            );
          })}

          {/* Photon sphere */}
          <circle
            cx="450"
            cy="290"
            r="90"
            fill="none"
            stroke="#c4b5fd"
            strokeWidth="1.5"
            strokeDasharray="7 5"
            opacity={0.28 + 0.12 * Math.sin(frame * 0.06)}
          />

          {/* Event horizon */}
          <circle cx="450" cy="290" r="60" fill="url(#bhCenter)" stroke="#8b5cf6" strokeWidth="3" />
          <circle cx="450" cy="290" r="63" fill="none" stroke="#8b5cf640" strokeWidth="1.8" strokeDasharray="6 4" />

          {/* Inner glow pulse */}
          <circle cx="450" cy="290" r="68" fill="none" stroke="#a78bfa" strokeWidth="1.2" opacity={0.12 + 0.1 * Math.sin(frame * 0.1)} />

          {/* Singularity */}
          <circle cx="450" cy="290" r="7" fill="#ffffff" opacity={0.5 + 0.25 * Math.sin(frame * 0.08)} />

          {/* Labels */}
          <text x="450" y="382" fill="#c4b5fd" fontSize="24" textAnchor="middle" fontWeight="700">
            事件视界
          </text>
          <line x1="450" y1="355" x2="450" y2="370" stroke="#c4b5fd" strokeWidth="1.5" />

          <text x="715" y="288" fill="#fbbf24" fontSize="24" fontWeight="700">
            吸积盘
          </text>
          <line x1="684" y1="286" x2="703" y2="286" stroke="#fbbf24" strokeWidth="1.5" />

          <text x="365" y="206" fill="#c4b5fd" fontSize="24" textAnchor="middle" fontWeight="600" opacity="0.6">
            光子球
          </text>
          <line x1="386" y1="210" x2="400" y2="228" stroke="#c4b5fd" strokeWidth="1.2" opacity="0.5" />

          {/* Jet streams */}
          <g opacity={interpolate(frame, [svgStart + 50, svgStart + 80], [0, 0.7], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
            <path d="M443,230 L438,30 M450,230 L450,14 M457,230 L462,30" stroke="url(#jetUp)" strokeWidth="3" opacity="0.6" />
            <path d="M447,228 L445,90 M453,228 L455,90" stroke="#c4b5fd" strokeWidth="1.2" opacity="0.25" />
            <path d="M443,350 L438,550 M450,350 L450,566 M457,350 L462,550" stroke="url(#jetDown)" strokeWidth="3" opacity="0.6" />
            <path d="M447,352 L445,490 M453,352 L455,490" stroke="#c4b5fd" strokeWidth="1.2" opacity="0.25" />
            <text x="472" y="26" fill="#a78bfa" fontSize="24" fontWeight="700">
              相对论性喷流
            </text>
            <text x="472" y="562" fill="#a78bfa" fontSize="24" fontWeight="700">
              相对论性喷流
            </text>
          </g>
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Famous Black Holes + Facts ===== */}
      <div style={{ position: 'absolute', top: 130, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          著名黑洞
        </div>
        {p.famousBlackHoles.map((fh, i) => {
          const s = spring({
            frame: frame - (famousStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#150a2e',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #5b21b640',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: '#c4b5fd' }}>
                  {fh.name}
                </span>
                <span style={{ fontSize: 20, color: '#64748b' }}>{fh.year}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <span style={{ fontSize: 20, color: '#a78bfa', fontWeight: 700 }}>
                  {fh.mass}
                </span>
                <span style={{ fontSize: 20, color: '#64748b' }}>距离: {fh.distance}</span>
              </div>
            </div>
          );
        })}

        {/* Black Hole Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            黑洞现象
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.blackHoleFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#150a2e',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #5b21b640',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: 26 }}>{fact.icon}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#a78bfa' }}>
                      {fact.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#150a2e',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #5b21b640',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 900, color: '#8b5cf6' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#150a2e',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #5b21b6',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24, marginTop: -20 }}>🕳️</span>
          <span style={{ fontSize: 20, color: '#ede9fe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #5b21b6, #8b5cf6, #ddd6fe)',
        }}
      />
    </AbsoluteFill>
  );
};
